import { FunctionComponent, useMemo } from 'react';
import VestingContext, {
  VestingContextValue,
} from '../contexts/VestingContext';
import useVestingAmountRemainingByWalletInBearsContractCall from '../hooks/bc/useVestingAmountRemainingByWalletInBearsContractCall';
import useVestingAmountRemainingByWalletInJarsContractCall from '../hooks/bc/useVestingAmountRemainingByWalletInJarsContractCall';
import useConnectedAccount from '../hooks/useConnectedAccount';
import FeatureFlag from '../types/featureFlag';
import { isFeatureEnabled } from '../utils/featueFlagsUtils';

const VestingProvider: FunctionComponent = ({ children }) => {
  const [walletAddress] = useConnectedAccount();

  const [
    vestingAmountInBearsRemaining,
    vestingAmountInBearsRemainingSum,
    readVestingAmountRemainingInBears,
  ] = useVestingAmountRemainingByWalletInBearsContractCall(
    walletAddress,
    !isFeatureEnabled(FeatureFlag.VestedHoneyInBearsPurchases)
  );

  const [
    vestingAmountInJarsRemaining,
    vestingAmountInJarsRemainingSum,
    readVestingAmountRemainingInJars,
  ] = useVestingAmountRemainingByWalletInJarsContractCall(
    walletAddress,
    !isFeatureEnabled(FeatureFlag.VestedHoneyInJarsPurchases)
  );

  const vestingAmountRemainingSum = useMemo(
    () =>
      vestingAmountInBearsRemainingSum &&
      vestingAmountInJarsRemainingSum &&
      vestingAmountInBearsRemainingSum.add(vestingAmountInJarsRemainingSum),
    [vestingAmountInBearsRemainingSum, vestingAmountInJarsRemainingSum]
  );

  const isLoading =
    (isFeatureEnabled(FeatureFlag.VestedHoneyInBearsPurchases) &&
      vestingAmountInBearsRemaining === undefined) ||
    (isFeatureEnabled(FeatureFlag.VestedHoneyInJarsPurchases) &&
      vestingAmountInJarsRemaining === undefined);

  const contextValue = useMemo<VestingContextValue>(
    () => ({
      vestingAmountInBearsRemaining,
      vestingAmountInBearsRemainingSum,
      readVestingAmountRemainingInBears,
      vestingAmountInJarsRemaining,
      vestingAmountInJarsRemainingSum,
      readVestingAmountRemainingInJars,
      vestingAmountRemainingSum,
      isLoading,
    }),
    [
      vestingAmountInBearsRemaining,
      vestingAmountInBearsRemainingSum,
      readVestingAmountRemainingInBears,
      vestingAmountInJarsRemaining,
      vestingAmountInJarsRemainingSum,
      readVestingAmountRemainingInJars,
      vestingAmountRemainingSum,
      isLoading,
    ]
  );

  return (
    <VestingContext.Provider value={contextValue}>
      {children}
    </VestingContext.Provider>
  );
};

export default VestingProvider;
